import React, {useCallback, useEffect, useRef, useState} from 'react';

const ResizableRightPanel: React.FC<{
    isOpen: boolean;
    children: React.ReactNode;
    /** Default width as fraction of window width (e.g. 0.4 = 40% of viewport). */
    defaultWidthRatio?: number;
    /** Minimum width in pixels (absolute floor). */
    minWidth?: number;
    /** Maximum width in pixels (cap so it doesn't eat the whole screen). */
    maxWidth?: number;
    /** Legacy: fixed pixel width (overrides defaultWidthRatio if set). */
    defaultWidth?: number;
}> = ({
    isOpen,
    children,
    defaultWidthRatio = 0.3,
    minWidth = 320,
    maxWidth = 1200,
    defaultWidth,
}) => {
    const RIGHT_PANEL_COLLAPSED = 30;

    // Compute initial width: explicit defaultWidth > ratio of window width
    const computeInitialWidth = () => {
        if (defaultWidth != null) return defaultWidth;
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
        return Math.max(minWidth, Math.min(maxWidth, viewportWidth * defaultWidthRatio));
    };

    const [rightWidth, setRightWidth] = useState(computeInitialWidth);
    const isResizingRight = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(0);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizingRight.current) return;
        e.preventDefault();
        const delta = dragStartX.current - e.clientX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, dragStartWidth.current + delta));
        // Direct DOM writes only — no React re-renders during drag
        if (containerRef.current) {
            containerRef.current.style.width = `${newWidth}px`;
        }
    }, [minWidth, maxWidth]);

    const stopResizing = useCallback(() => {
        if (isResizingRight.current) {
            isResizingRight.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            // Remove drag visual via DOM
            handleRef.current?.classList.remove('resizing');
            // Sync React state with final DOM width
            if (containerRef.current) {
                setRightWidth(containerRef.current.getBoundingClientRect().width);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopResizing);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [handleMouseMove, stopResizing]);

    const handleRightMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        dragStartX.current = e.clientX;
        dragStartWidth.current = containerRef.current?.getBoundingClientRect().width ?? rightWidth;
        isResizingRight.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        // Add drag visual via DOM — no setState, no re-render
        handleRef.current?.classList.add('resizing');
    };

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                .resize-handle.resizing { background: #e0e7ff; }
                .resize-handle.resizing .resize-line { background: #818cf8; }
            `}</style>
            {/* Resize handle — wider hit area with thin visible line */}
            <div
                ref={handleRef}
                onMouseDown={handleRightMouseDown}
                onDoubleClick={() => {
                    if (rightWidth <= RIGHT_PANEL_COLLAPSED + 10) {
                        setRightWidth(computeInitialWidth());
                    } else {
                        setRightWidth(RIGHT_PANEL_COLLAPSED);
                    }
                }}
                className="resize-handle w-2 cursor-col-resize flex items-center justify-center group shrink-0 hover:bg-slate-100 transition-colors"
            >
                <div className="resize-line w-px h-full bg-gray-200 group-hover:bg-gray-400 transition-colors" />
            </div>
            <div
                ref={containerRef}
                className="overflow-auto flex flex-col h-full bg-white border-l border-slate-200 shadow-inner shrink-0"
                style={{width: rightWidth}}
            >
                {children}
            </div>
        </>
    );
};

export default ResizableRightPanel;
